import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { BannerComponent } from '../../../../../shared/components/banner/banner.component';
import { environment } from '../../../../../../environments';
import { AuthorizationService } from '../../../../../core/services/authorization.service';
import {
  Authorization,
} from '../../../../../core/interfaces/authorization.interface';
import { catchError, finalize, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { AppState } from '../../../../../store/app.state';
import { User } from '../../../../../core/interfaces/user.interface';
import { selectCurrentUser } from '../../../../../store/auth/auth.selectors';

type ViewMode = 'given' | 'received';

@Component({
  selector: 'app-authorizations-liste',
  standalone: true,
  imports: [CommonModule, BannerComponent, FormsModule],
  templateUrl: './liste.component.html',
})
export class AuthorizationsListeComponent implements OnInit {
  isDppTheme = environment.theme.default === 'dpp';
  links = [{ label: 'Autorisations', href: undefined }];
  private store = inject(Store<AppState>);
  currentUser$!: Observable<User | null>;
  // View mode
  viewMode: ViewMode = 'given';

  // Pagination
  currentPage = 1;
  itemsPerPage = 10; 
  totalItems = 0;
  Math = Math;

  // Data and state
  givenAuthorizations: Authorization[] = []; // Autorisations données
  receivedAuthorizations: Authorization[] = []; // Demandes reçues
  loading = false;
  error: string | null = null;
  
  // Stocker les données originales pour accéder à l'OTP (uniquement pour "given")
  private givenAuthorizationsRaw: any[] = [];

  // Filters
  searchTerm = '';
  selectedStatus = 'PENDING'; // Par défaut, afficher les autorisations en attente

  // Available filter options
  statusOptions = [
    { value: 'PENDING', label: 'En attente' },
    { value: 'VALID', label: 'Validées' },
  ];

  constructor(
    private authorizationService: AuthorizationService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.currentUser$ = this.store.select(selectCurrentUser);

    // Lire le paramètre de route pour déterminer la vue
    this.route.params.subscribe((params: Params) => {
      const view = params['view'];
      if (view === 'given' || view === 'received') {
        this.viewMode = view;
      } else {
        // Si pas de paramètre, utiliser la vue par défaut (given)
        this.viewMode = 'given';
      }
    });

    this.currentUser$.subscribe((user) => {
      if (user) {
        this.loadData(user);
      }
    });
  }

  loadData(user: User): void {
    if (this.viewMode === 'given') {
      if (user.healthUniqueNumber?.number) {
        this.loadGivenAuthorizations(user.healthUniqueNumber.number);
      } else {
        console.warn('Numéro de santé unique non disponible pour charger les autorisations données');
        this.loading = false;
        // this.error = 'Numéro de santé unique non disponible';
        this.givenAuthorizations = [];
      }
    } else {
      this.loadReceivedAuthorizations();
    }
  }

  loadGivenAuthorizations(uniqueHealthNumber: string): void {
    this.loading = true;
    this.error = null;

    // S'assurer que selectedStatus est toujours défini (PENDING par défaut) et en majuscules
    const status = this.selectedStatus && this.selectedStatus.trim() !== '' 
      ? this.selectedStatus.toUpperCase() 
      : 'PENDING';

    const params = {
      status: status,
      page: this.currentPage,
      limit: this.itemsPerPage,
    };

    // if (!this.currentUser$?.uniqueHealthNumber?.number) {
    //   return null;
    // }
    // Appel API réel pour les autorisations données
    this.authorizationService
      .getGivenAuthorizations(uniqueHealthNumber, params)
      .pipe(
        catchError((error) => {
          console.error('Erreur lors du chargement des autorisations données:', error);
          this.error = 'Erreur lors du chargement des autorisations données';
          return of({ data: [], total: 0, page: 1, limit: 10, rawData: [] });
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((response) => {
        this.givenAuthorizations = response.data || [];
        this.totalItems = response.total || 0;
        // Stocker les données originales pour accéder à l'OTP
        this.givenAuthorizationsRaw = response.rawData || [];
        // Synchroniser la pagination avec la réponse de l'API
        if (response.page) {
          this.currentPage = response.page;
        }
        if (response.limit) {
          this.itemsPerPage = response.limit;
        }
      });
  }

  loadReceivedAuthorizations(): void {
    this.loading = true;
    this.error = null;

    const params = {
      status: 'active', // Filtrer uniquement les autorisations actives
      page: this.currentPage,
      limit: this.itemsPerPage,
    };

    // Appel API réel pour les Demandes reçues
    this.authorizationService
      .getReceivedAuthorizations(params)
      .pipe(
        catchError((error) => {
          console.error('Erreur lors du chargement des Demandes reçues:', error);
          this.error = 'Erreur lors du chargement des Demandes reçues';
          return of({ data: [], total: 0, page: 1, limit: 10 });
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((response) => {
        // Les Demandes reçues sont déjà filtrées par status=active côté API
        this.receivedAuthorizations = response.data || [];
        this.totalItems = response.total || 0;
        // Synchroniser la pagination avec la réponse de l'API
        if (response.page) {
          this.currentPage = response.page;
        }
        if (response.limit) {
          this.itemsPerPage = response.limit;
        }
      });
  }

  switchView(mode: ViewMode): void {
    if (this.viewMode !== mode) {
      this.viewMode = mode;
      this.currentPage = 1;
      this.searchTerm = '';
      // Réinitialiser à PENDING pour "given", pas de filtre pour "received"
      this.selectedStatus = mode === 'given' ? 'PENDING' : '';
      // Naviguer vers la route correspondante
      this.router.navigate(['authorizations', mode], { relativeTo: this.route.parent });
      // Utiliser take(1) pour obtenir la valeur actuelle de l'utilisateur
      this.currentUser$.pipe(take(1)).subscribe((user) => {
        if (user) {
          this.loadData(user);
        }
      });
    }
  }


  get currentAuthorizations(): Authorization[] {
    return this.viewMode === 'given' ? this.givenAuthorizations : this.receivedAuthorizations;
  }

  get filteredAuthorizations(): Authorization[] {
    let filtered = [...this.currentAuthorizations];

    // Filter by search term (filtrage côté client pour la recherche)
    if (this.searchTerm && this.searchTerm.trim() !== '') {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter((auth) =>
        (this.viewMode === 'given' ? auth.granteeName : auth.granterName)?.toLowerCase().includes(term) ||
        auth.meta.label?.toLowerCase().includes(term) ||
        auth.meta.notes?.toLowerCase().includes(term)
      );
    }

    // Le filtrage par statut est maintenant géré côté serveur via l'API
    // Pas besoin de filtrer côté client

    return filtered;
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  get pages(): number[] {
    const pages: number[] = [];
    const maxPages = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPages / 2));
    let endPage = Math.min(this.totalPages, startPage + maxPages - 1);

    if (endPage - startPage < maxPages - 1) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  changePage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.currentUser$.pipe(take(1)).subscribe((user) => {
        if (user) {
          this.loadData(user);
        }
      });
    }
  }

  onStatusChange(): void {
    // Recharger les données avec le nouveau filtre de statut
    this.currentPage = 1;
    this.currentUser$.pipe(take(1)).subscribe((user) => {
      if (user) {
        this.loadData(user);
      }
    });
  }

  onSearchChange(): void {
    // Pas besoin de faire quoi que ce soit ici
    // Le getter filteredAuthorizations se met à jour automatiquement
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'active':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800';
      case 'pending':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800';
      case 'expired':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800';
      case 'revoked':
      case 'rejected':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
      case 'approved':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800';
      default:
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'active':
        return 'Active';
      case 'pending':
        return 'En attente';
      case 'expired':
        return 'Expirée';
      case 'revoked':
        return 'Révoquée';
      case 'rejected':
        return 'Rejetée';
      case 'approved':
        return 'Approuvée';
      default:
        return status;
    }
  }

  getSourceBadgeClass(sourceType: string): string {
    switch (sourceType) {
      case 'system':
        return 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800';
      case 'user':
        return 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800';
      case 'external':
        return 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800';
      default:
        return 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800';
    }
  }

  getSourceText(sourceType: string): string {
    switch (sourceType) {
      case 'system':
        return 'Système';
      case 'user':
        return 'Utilisateur';
      case 'external':
        return 'Externe';
      default:
        return sourceType;
    }
  }

  formatDate(date: Date | string | undefined): string {
    if (!date) return 'N/A';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  revokeAuthorization(authorization: Authorization): void {
    if (!authorization.revocable) {
      alert('Cette autorisation ne peut pas être révoquée.');
      return;
    }

    if (
      confirm(
        `Êtes-vous sûr de vouloir révoquer l'autorisation pour ${authorization.granteeName} ?`
      )
    ) {
      this.loading = true;

      this.authorizationService
        .revokeAuthorization(authorization._id!)
        .pipe(
          catchError((error) => {
            console.error('Erreur lors de la révocation:', error);
            this.error = "Erreur lors de la révocation de l'autorisation";
            return of(null);
          }),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe(() => {
          // Si on est sur une page qui pourrait ne plus exister, revenir à la page 1
          // ou garder la page actuelle si elle existe toujours
          this.currentUser$.pipe(take(1)).subscribe((user) => {
            if (user) {
              this.loadData(user);
            }
          });
        });
    }
  }

  // Vérifier si une autorisation peut être validée
  canValidateAuthorization(authorization: Authorization): boolean {
    // Si le statut est "expired", on ne peut plus valider
    if (authorization.status === 'expired') {
      return false;
    }

    // Si le statut n'est pas "pending", on ne peut pas valider
    if (authorization.status !== 'pending') {
      return false;
    }

    // Trouver les données originales pour vérifier si l'OTP est encore valide
    const rawAuth = this.givenAuthorizationsRaw.find(
      (raw) => raw.identifier === authorization._id
    );


    if (!rawAuth) {
      // Si on ne trouve pas les données originales, on peut valider si le statut est pending
      // expiredAt dans authorization correspond à l'expiration de l'autorisation, pas de l'OTP
      // Donc on ne bloque pas sur authorization.expiresAt
      return true;
    }

    // Vérifier si l'OTP est encore valide (expiredAt dans rawAuth correspond à l'expiration de l'OTP)
    if (rawAuth.expiredAt) {
      const otpExpiredDate = new Date(rawAuth.expiredAt);
      const currentDate = new Date();
      // Si l'OTP est expiré, on ne peut plus valider
      if (otpExpiredDate < currentDate) {
        return false;
      }
    }

    // Si le statut est pending et l'OTP est valide, on peut valider
    return true;
  }

  // Valider une autorisation donnée (uniquement pour les "given")
  validateAuthorization(authorization: Authorization): void {
    if (!authorization._id) {
      alert('Impossible de trouver l\'identifiant de l\'autorisation.');
      return;
    }

    // Vérifier si on peut valider
    if (!this.canValidateAuthorization(authorization)) {
      alert('Cette autorisation ne peut plus être validée car elle a expiré.');
      return;
    }

    // Demander confirmation
    if (!confirm(`Voulez-vous valider l'autorisation pour ${authorization.granteeName} ?`)) {
      return;
    }

    // Récupérer le healthUniqueNumber de l'utilisateur courant
    this.currentUser$.pipe(take(1)).subscribe((user: User | null) => {
      if (!user || !user.healthUniqueNumber?.number) {
        alert('Impossible de récupérer le numéro de santé unique.');
        return;
      }

      this.loading = true;

      this.authorizationService
        .validateGivenAuthorization(authorization._id!, user.healthUniqueNumber.number)
        .pipe(
          catchError((error) => {
            console.error('Erreur lors de la validation:', error);
            const errorMessage =
              error.error?.message ||
              'Erreur lors de la validation de l\'autorisation';
            alert(errorMessage);
            return of(null);
          }),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe((result) => {
          if (result) {
            // Changer le filtre vers "VALID" pour voir l'autorisation validée
            this.selectedStatus = 'VALID';
            this.currentPage = 1;
            // Recharger les données avec le nouveau filtre
            this.loadData(user);
            alert('Autorisation validée avec succès !');
          }
        });
    });
  }

  refreshData(): void {
    this.currentUser$.pipe(take(1)).subscribe((user) => {
      if (user) {
        this.loadData(user);
      }
    });
  }

  formatPermissions(permissions: any[]): string {
    if (!permissions || permissions.length === 0) return '-';
    
    // Vérifier si on a un accès complet (resourceType: "*" et actions: ["*"])
    const hasFullAccess = permissions.some(
      p => p.resourceType === '*' && p.actions.includes('*')
    );
    
    if (hasFullAccess) {
      return 'Accès complet à toutes les ressources';
    }
    
    return permissions
      .map((p) => {
        const resourceLabel = this.formatResourceType(p.resourceType, p.scope);

        // Si le resourceType est spécifique mais actions contient "*"
        if (p.actions.includes('*') && p.resourceType !== '*') {
          return `${resourceLabel} (toutes les actions)`;
        }

        // Traduire les actions en français
        const translatedActions = p.actions.map((action: string) => 
          this.formatAction(action)
        ).join(', ');
        
        // Si c'est une ressource spécifique (avec resourceId), on peut l'indiquer
        const specificMarker = p.resourceId ? ' spécifique' : '';
        
        return `${resourceLabel}${specificMarker} (${translatedActions})`;
      })
      .join(' • ');
  }

  // Retourner les permissions sous forme de tableau pour affichage en liste
  getFormattedPermissionsList(permissions: any[]): string[] {
    if (!permissions || permissions.length === 0) return ['-'];
    
    // Vérifier si on a un accès complet (resourceType: "*" et actions: ["*"])
    const hasFullAccess = permissions.some(
      p => p.resourceType === '*' && p.actions.includes('*')
    );
    
    if (hasFullAccess) {
      return ['Accès complet à toutes les ressources'];
    }
    
    return permissions.map((p) => {
      const resourceLabel = this.formatResourceType(p.resourceType, p.scope);
      
      // Si le resourceType est spécifique mais actions contient "*"
      if (p.actions.includes('*') && p.resourceType !== '*') {
        return `${resourceLabel} (toutes les actions)`;
      }
      
      // Traduire les actions en français
      const translatedActions = p.actions.map((action: string) => 
        this.formatAction(action)
      ).join(', ');
      
      // Si c'est une ressource spécifique (avec resourceId), on peut l'indiquer
      const specificMarker = p.resourceId ? ' spécifique' : '';
      
      return `${resourceLabel}${specificMarker} (${translatedActions})`;
    });
  }

  private formatResourceType(resourceType: string, scope?: string): string {
    // Gestion spéciale pour les documents selon le scope
    if (resourceType === 'documents') {
      if (scope === 'financial') {
        return 'Documents financiers';
      } else if (scope === 'medical') {
        return 'Documents médicaux';
      }
      return 'Documents';
    }

    // Gestion spéciale pour les prestations selon le scope
    if (resourceType === 'prestations') {
      if (scope === 'financial') {
        return 'Prestations financières';
      } else if (scope === 'medical') {
        return 'Prestations médicales';
      }
      return 'Prestations';
    }

    const resourceTypeMap: { [key: string]: string } = {
      'medical_record': 'Dossier médical',
      'appointment': 'Rendez-vous',
      'prescription': 'Ordonnances',
      'lab_results': 'Résultats de laboratoire',
      'imaging': 'Imagerie médicale',
      'insurance_contract': 'Contrat d\'assurance',
      'insurance_contracts': 'Contrats d\'assurance',
      'financial': 'Données financières',
      'document': 'Documents',
      'family': 'Famille',
      'profile': 'Profil',
      'health_data': 'Données de santé',
      'vaccination': 'Vaccinations',
      'allergy': 'Allergies',
      'medical_history': 'Antécédents médicaux',
      '*': 'Toutes les ressources',
    };
    
    return resourceTypeMap[resourceType] || resourceType;
  }

  private formatAction(action: string): string {
    const actionMap: { [key: string]: string } = {
      'read': 'lecture',
      'create': 'création',
      'update': 'modification',
      'delete': 'suppression',
      'download': 'téléchargement',
      'share': 'partage',
      'cancel': 'annulation',
      'approve': 'approbation',
      'reject': 'rejet',
      'revoke': 'révocation',
      '*': 'toutes',
    };
    
    return actionMap[action] || action;
  }
}
